import { computed } from 'vue'
import type { ComputedRef } from 'vue'
import type { AppConfig } from '@/api/app-config'

type PlatformEmails = Pick<AppConfig['email'], 'info' | 'support'>
import { useAppConfig } from '@/composables/useAppConfig'
import { FALLBACK_COMPANY } from '@/constants/company'
import { buildStartlinePrivacyHtml } from '@/utils/startlinePrivacyContent'

// Used before (or without) the app-config response so contact details are always shown.
export const FALLBACK_EMAILS: PlatformEmails = {
  support: 'support@startline.be',
  info: 'info@startline.be',
}

// Single source for the platform mailboxes and the privacy policy HTML (issue #241).
export function usePlatformContact(): {
  emails: ComputedRef<PlatformEmails>
  infoEmail: ComputedRef<string>
  supportEmail: ComputedRef<string>
  privacyHtml: ComputedRef<string>
} {
  const { config } = useAppConfig()

  const emails = computed(() => config.value?.email ?? FALLBACK_EMAILS)
  const infoEmail = computed(() => config.value?.email?.info ?? FALLBACK_EMAILS.info)
  const supportEmail = computed(() => config.value?.email?.support ?? FALLBACK_EMAILS.support)
  const privacyHtml = computed(() =>
    buildStartlinePrivacyHtml(emails.value, config.value?.company ?? FALLBACK_COMPANY),
  )

  return { emails, infoEmail, supportEmail, privacyHtml }
}
